import React, { useEffect, useState } from 'react';
import DropdownComponent from '../../../fieldComponents/dropdownComponent/DropdownComponent';
import useStationApi from '../../../customHooks/useStationApi/useStationApi';
import usePrinterApi from '../../../customHooks/usePrinterApi/usePrinterApi';
import { useAuth } from '../../context/AuthContext/AuthContext';
import { useAppInsightConsumer } from '../../context/AppInsightContext/AppInsightContext';
import {
  createErrorTelemetryProperties,
  getErrorMessage,
} from '../../../utils/appInsightUtils';
import { AxiosError } from 'axios';
import PrinterModuleProp from '../../../@types/PrinterModule.type';
import {
  appStateActions,
  useAppDispatch,
  useAppSelector,
} from '../../../store/store';
import { Box } from '@mui/material';
import PrinterData from '../../../@types/PrinterData.type';

const PrinterModule: React.FC<PrinterModuleProp> = ({
  stationEndpoint,
  printerEndpoint,
  printerType,
  stationDropDownLabel,
  printerDropDownLabel,
}) => {
  const [stationDatas, setStationDatas] = useState<string[] | undefined>(
    undefined,
  );
  const [printerDatas, setPrinterDatas] = useState<string[] | undefined>(
    undefined,
  );

  const [printerCodeAndNetworkData, setPrinterCodeAndNetworkData] = useState<
    PrinterData[] | undefined
  >(undefined);

  const { userData } = useAuth();
  const { logInformation, logError, trackError } = useAppInsightConsumer();

  const { selectStationData, selectPrinterData, reqValidity } = useAppSelector(
    (state) => state.appStateReducer,
  );

  const dispatch = useAppDispatch();

  const handleChange = (code: string) => {
    // First update printer code state
    dispatch(appStateActions.handlePrinterChange(code));

    // Then update the network path state
    dispatch(
      appStateActions.updateNetworkPath(
        printerCodeAndNetworkData
          ?.find((item) => item.code === code)
          ?.networkPath?.trim(),
      ),
    );
  };
  // Calling APIM GetLaserStations endpoint.
  const {
    isError: isStationError,
    isFetching: isStationFetching,
    isLoading: isStationLoading,
    isSuccess: isStationSuccess,
    isPaused: isStationPaused,
    data: stationData,
    error: stationError,
  } = useStationApi(stationEndpoint, printerType);

  // Calling APIM GetLaserPrinters endpoint. This will only be called when
  // station will be chosen from the dropdown by the user
  const {
    isError: isPrinterError,
    isFetching: isPrinterFetching,
    isLoading: isPrinterLoading,
    isSuccess: isPrinterSuccess,
    isPaused: isPrinterPaused,
    data: printerData,
    error: printerError,
    refetch: printerRefetching, // refetch() can be used as enabled:false in useQuery
  } = usePrinterApi(printerEndpoint, printerType);

  // Once the user selects the station printer API gets called
  useEffect(() => {
    // If the station Data is null or undefined basically if it's falsy then the printer API will not be
    // called
    if (selectStationData) {
      //AppInsight Logging
      logInformation(
        `Station ${selectStationData} is selected by user ${userData?.profile?.email}, now calling the printer API`,
        'PrinterModule',
        'useEffect',
      );

      // Call the printer API
      printerRefetching();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectStationData]);

  // When the printer API gets successfull
  useEffect(() => {
    if (isPrinterSuccess) {
      // If the printer API is successfull then reset the error state. This helps to clear any previous
      // error
      dispatch(appStateActions.updateErrorState(undefined));
      try {
        const parsedPrinterData = printerData
          .filter(
            (printer): printer is PrinterData =>
              typeof printer?.code === 'string' &&
              typeof printer?.networkPath === 'string',
          )
          .map((printer) => ({
            ...printer,
            code: printer.code.trim(),
            networkPath: printer.networkPath.trim(),
          }));

        // AppInsight Logging
        logInformation(
          `Response from ${printerEndpoint} endpoint with parameter { station : ${selectStationData} } 
           came up successfully for user ${userData?.profile?.email} and xml is successfully parsed`,
          'PrinterModule',
          'useEffect',
        );

        if (!parsedPrinterData) {
          logInformation(
            `Response from ${printerEndpoint} endpoint with parameter { station : ${selectStationData} } 
             is undefined for user ${userData?.profile?.email}`,
            'PrinterModule',
            'useEffect',
          );
        }

        if (parsedPrinterData?.length === 0) {
          logInformation(
            `Response from ${printerEndpoint} endpoint with parameter { station : ${selectStationData} } 
             have no data and returning empty array for user ${userData?.profile?.email}`,
            'PrinterModule',
            'useEffect',
          );

          setPrinterDatas([]);
          return;
        }

        setPrinterCodeAndNetworkData(parsedPrinterData as PrinterData[]);

        const code: string[] = [];

        (parsedPrinterData as PrinterData[])?.map((item) =>
          code?.push(item.code),
        );

        setPrinterDatas(code);
      } catch (err) {
        // AppInsight Logging
        logError(
          getErrorMessage(
            new Error(`Error while parsing the XML response from ${printerEndpoint} endpoint 
            for user ${userData?.profile?.email}`),
          ) as string,
          'PrinterModule',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(
            new Error(`Error while parsing the XML response from ${printerEndpoint} endpoint 
            for user ${userData?.profile?.email}`) as Error | AxiosError,
          ),
        );

        trackError(
          new Error(`Error while parsing the XML response from ${printerEndpoint} endpoint 
          for user ${userData?.profile?.email}`) as Error | AxiosError,
        );

        logError(
          getErrorMessage(err as Error | AxiosError) as string,
          'PrinterModule',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(err as Error | AxiosError),
        );

        trackError(err as Error | AxiosError);

        // Handling the error during XML Parsing
        dispatch(appStateActions.updateErrorState(err as Error));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinterSuccess, printerData]);

  // If there is any error during station API call. Can be network disconnect or 5xx / 4xx error from the apis
  useEffect(() => {
    // If Netowrk Error
    if (isPrinterPaused) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(
            `Network Error from ${printerEndpoint} endoint for ${userData?.profile?.email}`,
          ),
        ) as string,
        'PrinterModule',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(
            `Network Error from ${printerEndpoint} endoint for ${userData?.profile?.email}`,
          ),
        ),
      );
      trackError(
        new Error(
          `Network Error from ${printerEndpoint} endoint for ${userData?.profile?.email}`,
        ),
      );

      dispatch(
        appStateActions.updateErrorState(
          new Error(
            'You are not connected to any Network. Please connect to a network and try again',
          ),
        ),
      );
    }
    // If there is 5xx / 4xx response from the api
    else if (isPrinterError) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(
            `Error from ${printerEndpoint} endpoint for ${userData?.profile?.email}
            with parameter { station : ${selectStationData} }`,
          ),
        ) as string,
        'PrinterModule',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(
            `Error from ${printerEndpoint} endpoint for ${userData?.profile?.email}
            with parameter { station : ${selectStationData} }`,
          ),
        ),
      );

      trackError(
        new Error(
          `Error from ${printerEndpoint} endpoint for ${userData?.profile?.email} with parameter { station : ${selectStationData} }`,
        ),
      );

      logError(
        getErrorMessage(printerError as Error | AxiosError) as string,
        'PrinterModule',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(printerError),
      );

      trackError(printerError);

      dispatch(appStateActions.updateErrorState(printerError));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinterError, isPrinterPaused]);

  // When the station API gets successfull
  useEffect(() => {
    if (isStationSuccess) {
      // If the printer API is successfull then reset the error state. This helps to clear any previous
      // error
      dispatch(appStateActions.updateErrorState(undefined));
      try {
        // GetLaserStations response is JSON from APIM.
        const parsedStationData = stationData
          .filter((station): station is string => typeof station === 'string')
          .map((station) => station.trim());

        logInformation(
          `Response from ${stationEndpoint} endpoint came up successfully for 
          user ${userData?.profile?.email}`,
          'PrinterModule',
          'useEffect',
        );

        if (!stationData) {
          logInformation(
            `Response from ${printerEndpoint} endpoint } is undefined for user ${userData?.profile?.email}`,
            'PrinterModule',
            'useEffect',
          );
        }

        if (stationData?.length === 0) {
          logInformation(
            `Response from ${printerEndpoint} endpoint have no data and returning empty array for user ${userData?.profile?.email} and xml is successfully parsed`,
            'PrinterModule',
            'useEffect',
          );
        }

        setStationDatas(parsedStationData);
      } catch (err) {
        // AppInsight Logging
        logError(
          getErrorMessage(
            new Error(`Error while parsing the XML response from ${stationEndpoint} endpoint 
             with parameter for user ${userData?.profile?.email}`),
          ) as string,
          'PrinterModule',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(
            new Error(`Error while parsing the XML response from ${stationEndpoint} endpoint 
              with parameter for user ${userData?.profile?.email}`),
          ),
        );

        trackError(
          new Error(`Error while parsing the XML response from ${stationEndpoint} endpoint 
            with parameter for user ${userData?.profile?.email}`),
        );

        logError(
          getErrorMessage(err as Error | AxiosError) as string,
          'PrinterModule',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(err as Error | AxiosError),
        );

        trackError(err as Error | AxiosError);
        // Handle the error during XML Parsing
        dispatch(appStateActions.updateErrorState(err as Error));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationData, isStationSuccess]);

  // If there is any error during station API call. Can be netowk disconnect or 5xx / 4xx error from the api
  useEffect(() => {
    // If Network Disconnectivity
    if (isStationPaused) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(
            `Network Error from ${stationEndpoint} endoint for ${userData?.profile?.email}`,
          ),
        ) as string,
        'PrinterModule',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(
            `Network Error from ${stationEndpoint} endoint for ${userData?.profile?.email}`,
          ),
        ),
      );
      trackError(
        new Error(
          `Network Error from ${stationEndpoint} endoint for ${userData?.profile?.email}`,
        ),
      );

      dispatch(
        appStateActions.updateErrorState(
          new Error(
            'You are not connected to any Network. Please connect to a network and try again.',
          ),
        ),
      );
    }
    // If there is 5xx / 4xx response from the api
    else if (isStationError) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(
            `Error from ${stationEndpoint} endpoint for ${userData?.profile?.email}`,
          ),
        ) as string,
        'PrinterModule',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(
            `Error from ${stationEndpoint} endpoint for ${userData?.profile?.email}`,
          ),
        ),
      );

      trackError(
        new Error(
          `Error from ${stationEndpoint} endpoint for ${userData?.profile?.email}`,
        ),
      );

      logError(
        getErrorMessage(stationError as Error | AxiosError) as string,
        'PrinterModule',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(stationError),
      );

      trackError(stationError as Error | AxiosError);

      dispatch(appStateActions.updateErrorState(stationError));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStationError, isStationPaused]);

  // Before the component gets unmounted reset the station Data and Priner Data
  useEffect(() => {
    dispatch(appStateActions.resetStationAndPrinterValidationError());
    return () => {
      dispatch(appStateActions.resetStationData());
      dispatch(appStateActions.resetPrinterData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Box width="100%" position="relative">
        {/* If the Api is loading the show a loader within the dropdown */}
        {(isStationLoading || isStationFetching) && (
          <auro-loader
            ringworm
            onLight
            data-testid="station-loader"
          ></auro-loader>
        )}

        <DropdownComponent
          labelId={`${printerType.toLowerCase()}-city-select-label`}
          id={`${printerType.toLowerCase()}-city-select`}
          label={stationDropDownLabel}
          menuItems={stationDatas}
          disabled={
            // Will be disabled when the API is in a loading sate or there is any error during
            // API call
            isStationLoading ||
            isStationFetching ||
            isStationError ||
            isStationPaused
          }
          handleChange={appStateActions.handleStationChange}
          error={!reqValidity.selectStation}
          // The conditions are added in value to get rid of the warning in MUI : "Select set value is out of
          // range".
          // Ref : https://stackoverflow.com/questions/76159113/material-ui-you-have-provided-an-out-of-range-value-for-the-select-component-d
          value={
            // If the station Data is undefined or null and if the array for tthe station Data is of length 0
            // then value should be ''
            !stationDatas || stationDatas.length === 0
              ? ''
              : // If the station Data selected is undefined or null then value should be ''
                selectStationData ?? ''
          }
        />
      </Box>

      {/* "Printer" Dropdown */}
      <Box width="100%" position="relative">
        {/* If the Api is loading the show a loader within the dropdown */}
        {(isPrinterLoading || isPrinterFetching) && (
          <auro-loader ringworm onLight></auro-loader>
        )}
        <DropdownComponent
          labelId={`${printerType.toLowerCase()}-printer-select-label`}
          id={`${printerType.toLowerCase()}-printer-select`}
          label={printerDropDownLabel}
          menuItems={printerDatas}
          disabled={
            // Will be disabled when the API is in a loading sate or there is any error durings
            // API call and also till the user selects the station from the dropdown
            selectStationData === undefined ||
            isPrinterLoading ||
            isPrinterFetching ||
            isPrinterPaused ||
            isPrinterError
          }
          handlePrinterCodeChange={handleChange}
          error={!reqValidity.selectPrinter}
          // The conditions are added in value to get rid of the warning in MUI : "Select set value is out of
          // range".
          // Ref : https://stackoverflow.com/questions/76159113/material-ui-you-have-provided-an-out-of-range-value-for-the-select-component-d
          value={
            // If the printer Data is undefined or null and if the array for the station Data is of length 0
            // then value should be ''
            !printerDatas || printerDatas.length === 0
              ? ''
              : // If the printer Data selected is undefined or null then value should be ''
                selectPrinterData ?? ''
          }
        />
      </Box>
    </>
  );
};

export default PrinterModule;
